import { useMemo, useState } from 'react'
import { Plus, Pencil, Trash2, type LucideIcon } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { PageHeader } from '@/components/ui/page-header'
import { EmptyState } from '@/components/ui/empty-state'
import { cn } from '@/lib/utils'
import { useBalances, useCardTotalDebt } from '@/hooks/useAccounts'
import { useCurrentRates, convertWithRates } from '@/hooks/useCurrentRates'
import { useNetWorthItems, useDeleteNetWorthItem } from '@/hooks/useNetWorth'
import { useHousehold } from '@/context/HouseholdContext'
import { useConfirm } from '@/context/ConfirmProvider'
import { formatCurrency } from '@/lib/currency'
import { ConversionIndicator } from '@/components/ConversionIndicator'
import { netWorthType } from '@/lib/networth'
import { accountIcon } from '@/lib/accountIcon'
import { netWorthTotals } from '@/lib/finance'
import { NetWorthItemDialog } from '@/components/networth/NetWorthItemDialog'
import { useT } from '@/i18n/I18nProvider'
import type { NetWorthItem, NetWorthKind } from '@/types/db'

interface Entry {
  key: string
  name: string
  group: string
  valueBase: number | null // sempre positivo (magnitude); sinal é dado pela seção
  displayValue: number
  displayCurrency: string
  source: 'account' | 'card' | 'manual'
  item?: NetWorthItem
  // Contas/cartões já mostram o emoji no nome — não usam ícone lucide (evita duplicar).
  // Só itens manuais de patrimônio têm ícone por tipo.
  icon?: LucideIcon
  iconColor?: string
}

export function NetWorthPage() {
  const { t } = useT()
  const { baseCurrency } = useHousehold()
  const { data: balances } = useBalances()
  const { data: cardDebt } = useCardTotalDebt()
  const { data: rates } = useCurrentRates()
  const { data: items } = useNetWorthItems()
  const del = useDeleteNetWorthItem()
  const confirm = useConfirm()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<NetWorthItem | null>(null)
  const [defaultKind, setDefaultKind] = useState<NetWorthKind>('asset')

  const { assets, liabilities, anyMissing: entriesMissing } = useMemo(() => {
    const assets: Entry[] = []
    const liabilities: Entry[] = []
    let anyMissing = false

    for (const b of balances ?? []) {
      if (b.account.type === 'cartao') {
        // Patrimônio usa a DÍVIDA TOTAL do cartão (todos os status/datas — compras
        // lançadas são dívida real). Negativo = dívida (passivo); positivo = crédito (ativo).
        const cardBal = cardDebt?.get(b.account.id) ?? b.balance
        if (cardBal < -0.005) {
          const owed = -cardBal
          const conv = convertWithRates(owed, b.account.currency, rates)
          if (conv == null) anyMissing = true
          liabilities.push({
            key: 'card-' + b.account.id,
            name: `${accountIcon(b.account)} ${b.account.name}`,
            group: t('rep.nwGroupCards'),
            valueBase: conv,
            displayValue: owed,
            displayCurrency: b.account.currency,
            source: 'card',
          })
        } else if (cardBal > 0.005) {
          const conv = convertWithRates(cardBal, b.account.currency, rates)
          if (conv == null) anyMissing = true
          assets.push({
            key: 'card-' + b.account.id,
            name: `${accountIcon(b.account)} ${b.account.name} (${t('rep.nwCreditSuffix')})`,
            group: t('rep.nwGroupCards'),
            valueBase: conv,
            displayValue: cardBal,
            displayCurrency: b.account.currency,
            source: 'card',
          })
        }
      } else {
        const conv = convertWithRates(b.balance, b.account.currency, rates)
        if (conv == null) anyMissing = true
        assets.push({
          key: 'acc-' + b.account.id,
          name: `${accountIcon(b.account)} ${b.account.name}`,
          group: t('rep.nwGroupAccounts'),
          valueBase: conv,
          displayValue: b.balance,
          displayCurrency: b.account.currency,
          source: 'account',
        })
      }
    }

    for (const it of items ?? []) {
      const conv = convertWithRates(it.value, it.currency, rates)
      if (conv == null) anyMissing = true
      const type = netWorthType(it.item_type)
      const entry: Entry = {
        key: 'nw-' + it.id,
        name: it.name,
        group: type.label,
        valueBase: conv,
        displayValue: it.value,
        displayCurrency: it.currency,
        source: 'manual',
        item: it,
        icon: type.icon,
        iconColor: type.color,
      }
      if (it.kind === 'asset') assets.push(entry)
      else liabilities.push(entry)
    }

    return { assets, liabilities, anyMissing }
  }, [balances, cardDebt, rates, items, t])

  // Totais consolidados via a MESMA função pura testada (finance.netWorthTotals).
  const { totalAssets, totalLiabilities, netWorth, anyMissing: totalsMissing } = useMemo(
    () =>
      netWorthTotals(
        (balances ?? []).map((b) => ({
          type: b.account.type,
          currency: b.account.currency,
          // cartão: usa dívida TOTAL (all-status); demais: saldo corrente
          balance: b.account.type === 'cartao' ? (cardDebt?.get(b.account.id) ?? b.balance) : b.balance,
        })),
        (items ?? []).map((it) => ({ kind: it.kind, value: it.value, currency: it.currency })),
        baseCurrency,
        rates,
      ),
    [balances, cardDebt, items, baseCurrency, rates],
  )

  // "* parcial": alguma moeda sem taxa em cache, seja nas linhas exibidas ou no total.
  const anyMissing = entriesMissing || totalsMissing

  function openNew(kind: NetWorthKind) {
    setEditing(null)
    setDefaultKind(kind)
    setDialogOpen(true)
  }
  function openEdit(it: NetWorthItem) {
    setEditing(it)
    setDialogOpen(true)
  }
  async function remove(it: NetWorthItem) {
    const ok = await confirm({
      title: t('rep.nwDeleteConfirmTitle', { name: it.name }),
      description: t('rep.nwDeleteConfirmDesc'),
      confirmText: t('common.delete'),
      tone: 'destructive',
    })
    if (!ok) return
    try {
      await del.mutateAsync(it.id)
      toast.success(t('rep.nwItemDeleted'))
    } catch (e) {
      toast.error(t('rep.nwDeleteError'), { description: (e as Error).message })
    }
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <PageHeader
        title={t('rep.nwTitle')}
        subtitle={t('rep.nwSubtitle', { currency: baseCurrency })}
      />

      {/* Net worth total */}
      <Card className="border-primary/40">
        <CardContent className="p-5">
          <div className="text-muted-foreground text-sm">{t('rep.nwNetWorthLabel')}</div>
          <div className={cn('text-4xl font-bold tabular-nums', netWorth < 0 ? 'text-destructive' : 'text-foreground')}>
            {formatCurrency(netWorth, baseCurrency)}
            {anyMissing && <span className="text-muted-foreground text-sm"> *</span>}
          </div>
          <div className="text-muted-foreground mt-2 flex gap-4 text-sm">
            <span className="text-success">{t('rep.nwAssets')} {formatCurrency(totalAssets, baseCurrency)}</span>
            <span className="text-destructive">{t('rep.nwLiabilities')} {formatCurrency(totalLiabilities, baseCurrency)}</span>
          </div>
          {anyMissing && <div className="text-muted-foreground mt-1 text-xs">{t('rep.nwPartial')}</div>}
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Section
          title={t('rep.nwAssets')}
          total={totalAssets}
          baseCurrency={baseCurrency}
          rates={rates}
          entries={assets}
          positive
          onAdd={() => openNew('asset')}
          onEdit={openEdit}
          onRemove={remove}
        />
        <Section
          title={t('rep.nwLiabilities')}
          total={totalLiabilities}
          baseCurrency={baseCurrency}
          rates={rates}
          entries={liabilities}
          positive={false}
          onAdd={() => openNew('liability')}
          onEdit={openEdit}
          onRemove={remove}
        />
      </div>

      <NetWorthItemDialog open={dialogOpen} onOpenChange={setDialogOpen} item={editing} defaultKind={defaultKind} />
    </div>
  )
}

function Section({
  title,
  total,
  baseCurrency,
  rates,
  entries,
  positive,
  onAdd,
  onEdit,
  onRemove,
}: {
  title: string
  total: number
  baseCurrency: string
  rates: Map<string, number> | undefined
  entries: Entry[]
  positive: boolean
  onAdd: () => void
  onEdit: (it: NetWorthItem) => void
  onRemove: (it: NetWorthItem) => void
}) {
  const { t } = useT()
  // agrupa por group
  const groups = useMemo(() => {
    const map = new Map<string, Entry[]>()
    for (const e of entries) {
      if (!map.has(e.group)) map.set(e.group, [])
      map.get(e.group)!.push(e)
    }
    // dentro de cada grupo: maior valor no topo (por magnitude na base)
    for (const rows of map.values())
      rows.sort((a, b) => Math.abs(b.valueBase ?? 0) - Math.abs(a.valueBase ?? 0) || a.name.localeCompare(b.name))
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]))
  }, [entries])

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">
          {title}{' '}
          <span className={cn('tabular-nums', positive ? 'text-success' : 'text-destructive')}>
            {formatCurrency(total, baseCurrency)}
          </span>
        </CardTitle>
        <Button variant="outline" size="sm" onClick={onAdd}>
          <Plus className="size-4" /> {t('common.add')}
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        {entries.length === 0 ? (
          <EmptyState
            title={t('rep.nwSectionEmptyTitle')}
            description={t('rep.nwSectionEmptyDesc', { name: title.toLowerCase() })}
            action={
              <Button variant="outline" size="sm" onClick={onAdd}>
                <Plus className="size-4" /> {t('common.add')}
              </Button>
            }
          />
        ) : (
          <div className="divide-border divide-y">
            {groups.map(([group, rows]) => {
              const subtotal = rows.reduce((s, e) => s + (e.valueBase ?? 0), 0)
              return (
                <div key={group}>
                  <div className="bg-muted/40 flex items-center justify-between px-4 py-1.5 text-xs font-medium">
                    <span>{group}</span>
                    <span className="tabular-nums">{formatCurrency(subtotal, baseCurrency)}</span>
                  </div>
                  {rows.map((e) => (
                    <div key={e.key} className="hover:bg-accent/30 flex items-center gap-2 px-4 py-2 text-sm">
                      {e.icon && <e.icon className={cn('size-4 shrink-0', e.iconColor)} />}
                      <span className="flex-1 truncate">{e.name}</span>
                      {e.source !== 'manual' && (
                        <Badge variant="secondary" className="shrink-0 text-xs">
                          {e.source === 'card' ? t('rep.nwBadgeCard') : t('rep.nwBadgeAccount')}
                        </Badge>
                      )}
                      <ConversionIndicator
                        amount={e.displayValue}
                        currency={e.displayCurrency}
                        baseCurrency={baseCurrency}
                        rates={rates}
                      />
                      <span className="tabular-nums whitespace-nowrap">
                        {formatCurrency(e.displayValue, e.displayCurrency)}
                      </span>
                      {e.source === 'manual' && e.item && (
                        <div className="flex gap-0.5">
                          <Button variant="ghost" size="icon" className="size-7" onClick={() => onEdit(e.item!)} aria-label={t('common.edit')}>
                            <Pencil className="size-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="size-7" onClick={() => onRemove(e.item!)} aria-label={t('common.delete')}>
                            <Trash2 className="text-destructive size-3.5" />
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
